/* eslint-disable import/no-unresolved */
import { Footer as StbFooter } from 'Components/Footer';
import { RouteName } from 'Features/router';
import {
  Addon,
  Bottom,
  Column,
  Copyright,
  Finansportalen,
  Footer,
  Top,
} from 'footer/components';
import {
  Cooperation,
  Group,
  Logo,
  Logout,
  Menu,
  Navbar,
} from 'navbar/components';

import { useLayoutContext } from 'App/LayoutContextProvider/LayoutContextProvider.hooks';
import { FormHeader } from 'Forms/ChangeForm/components/FormHeader/FormHeader';
import React from 'react';
import { isOfflineMode } from 'Utils/isOfflineMode.utils';
import { getBasePath } from 'Utils/navigation.utils';
import { getAppText } from 'Utils/text';
import { FooterCooperation } from '../FooterCooperation';
import {
  getPartnerBrandFromBaseUrl,
  isDanskeBankBrand,
} from '../themes.helpers';
import { FooterFlexContainer, FooterLink } from '../themes.styles';
import {
  checkIsPartnerBrand,
  externalLink,
  getFooterAddon,
  getNavbarLogo,
} from './partnerTheme.helpers';
import {
  useGetNavbarCooperation,
  useMenuNavigation,
} from './partnerTheme.hooks';

export const AppNavbar = (): React.ReactElement | null => {
  const { isRebrandedQuery } = useLayoutContext();
  const currentBasename = getBasePath();
  const brand = getPartnerBrandFromBaseUrl(currentBasename);
  const isPartnerBrand = checkIsPartnerBrand(currentBasename);
  const navigate = useMenuNavigation();
  const logo = getNavbarLogo(brand);
  const cooperation = useGetNavbarCooperation(brand);
  const isDanskeBank = isDanskeBankBrand(brand);

  if (isOfflineMode()) {
    return null;
  }

  if (isRebrandedQuery) {
    return <FormHeader />;
  }

  if (!isPartnerBrand) {
    return <Navbar />;
  }

  return (
    <Navbar>
      {logo && <Logo logo={logo} />}
      <Group>
        <Logout />
        <Menu>
          <Menu.Link onClick={() => navigate('/')}>
            {getAppText('partner.navbar.menu.insuranceOverview')}
          </Menu.Link>
          <Menu.Link onClick={() => navigate(RouteName.DOCUMENT_ARCHIVE)}>
            {getAppText('partner.navbar.menu.insuranceDocuments')}
          </Menu.Link>
          <Menu.Link onClick={() => navigate(RouteName.INVOICE_ARCHIVE)}>
            {getAppText('partner.navbar.menu.invoiceArchive')}
          </Menu.Link>
          <Menu.Link href={externalLink('arkiv')} target="_blank">
            {getAppText('partner.navbar.menu.documentArchive.external')}
          </Menu.Link>
          {isDanskeBank && (
            <Menu.Link href={externalLink('micro/kontakt-oss')} target="_blank">
              {getAppText('partner.navbar.menu.contactUs.external')}
            </Menu.Link>
          )}
          {!isDanskeBank && (
            <Menu.Link onClick={() => navigate(RouteName.CONTACT_US)}>
              {getAppText('partner.navbar.menu.contactUs')}
            </Menu.Link>
          )}
        </Menu>
      </Group>
      <Cooperation {...cooperation} />
    </Navbar>
  );
};

export const AppFooter = (): React.ReactElement | null => {
  const currentBasename = getBasePath();
  const brand = getPartnerBrandFromBaseUrl(currentBasename);
  const isPartnerBrand = checkIsPartnerBrand(currentBasename);
  const addon = getFooterAddon(brand);

  if (isOfflineMode()) {
    return null;
  }

  if (!isPartnerBrand) {
    return <StbFooter brand={brand} />;
  }

  return (
    <Footer>
      <Top />
      <Bottom>
        <FooterFlexContainer>
          <Column>
            <FooterLink
              href="https://storebrand.no/om-storebrand/sikkerhet-og-personvern"
              target="_blank"
            >
              {getAppText('partnerFooter.personalDataLink.text')}
            </FooterLink>
            <FooterLink href={externalLink('kundeprofil')}>
              {getAppText('partnerFooter.profileLink.text')}
            </FooterLink>
            <Finansportalen />
            <Copyright />
          </Column>
          <Column>
            <Addon logo={{ ...addon }} />
            <FooterCooperation brand={brand} />
          </Column>
        </FooterFlexContainer>
      </Bottom>
    </Footer>
  );
};
